/**
 * Offline-first cache for the Food Scanner.
 *
 * Stores every successful scan on the device (localStorage) so the user can:
 *   • rescan the same barcode in-store with no signal and still see the verdict
 *   • browse everything they've already scanned ("Safe", "Watch out", "Avoid")
 *   • queue new scans while offline and auto-process them on reconnect
 */

const SAVED_KEY = "scm_saved_scans_v1";
const QUEUE_KEY = "scm_pending_scans_v1";
const MAX_SAVED = 500;
const MAX_QUEUE = 20;

const read = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ }
};

const makeKey = (scan, extra = {}) => {
  const bc = (extra.barcode || scan?.barcode || "").trim();
  if (bc) return `bc:${bc}`;
  const name = (scan?.product_name || "").trim().toLowerCase();
  const brand = (scan?.brand || "").trim().toLowerCase();
  if (name) return `nm:${brand}|${name}`;
  return `rnd:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
};

export const scanCache = {
  save(scan, extra = {}) {
    if (!scan) return null;
    const key = makeKey(scan, extra);
    const items = read(SAVED_KEY).filter((x) => x.key !== key);
    const entry = {
      key,
      barcode: extra.barcode || scan.barcode || null,
      product_name: scan.product_name || "Unknown product",
      brand: scan.brand || null,
      verdict: scan.verdict || null,
      traffic_light: scan.traffic_light || null,
      allergen_match: scan.allergen_match || null,
      nasties_match: scan.nasties_match || null,
      image_url: scan.image_url || null,
      scan,
      saved_at: new Date().toISOString(),
    };
    items.unshift(entry);
    write(SAVED_KEY, items.slice(0, MAX_SAVED));
    return entry;
  },

  getByBarcode(barcode) {
    if (!barcode) return null;
    return read(SAVED_KEY).find((x) => x.barcode === String(barcode).trim()) || null;
  },

  list() {
    return read(SAVED_KEY);
  },

  remove(key) {
    write(SAVED_KEY, read(SAVED_KEY).filter((x) => x.key !== key));
  },

  clearAll() {
    write(SAVED_KEY, []);
  },

  // ---- Offline queue ----
  enqueue(payload) {
    const q = read(QUEUE_KEY);
    const entry = { id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, payload, queued_at: new Date().toISOString() };
    q.unshift(entry);
    write(QUEUE_KEY, q.slice(0, MAX_QUEUE));
    return entry;
  },

  queue() {
    return read(QUEUE_KEY);
  },

  dequeue(id) {
    write(QUEUE_KEY, read(QUEUE_KEY).filter((x) => x.id !== id));
  },

  queueCount() {
    return read(QUEUE_KEY).length;
  },
};

export const isNetworkError = (err) => {
  if (!err) return false;
  if (err.message === "Network Error") return true;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return false;
};
